// component.jsx — CardRequirementsComponent (UI principal)
import { useMemo } from "preact/hooks";
import { useRequirementsCard } from "./store";
import {
  Box, Typography, Chip, Divider, List, ListItem, ListItemText,
  ListItemIcon, IconButton, Tooltip, Stack, Button, Card, CardContent
} from "@mui/material";
import { styled } from "@mui/material/styles";
import DescriptionIcon from "@mui/icons-material/Description";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CategoryIcon from "@mui/icons-material/Category";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useTheme } from "@mui/material";

const StyledCardRequirements = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderLeft: `4px solid ${theme.palette.primary.main}`,
  borderRadius: 8,
  overflow: "hidden",
}));

const statusChip = (name) => {
  switch ((name || "").toLowerCase()) {
    case "confirmed":
      return { variant: "filled", color: "success", label: "Confirmado" };
    case "canceled":
      return { variant: "outlined", color: "error", label: "Cancelado" };
    default:
      return { variant: "outlined", color: "warning", label: "Creado" };
  }
};

export const CardRequirementsComponent = () => {
  const {
    quotation,
    role: viewerRole,
    openCreateModal,
    openDeleteModal,
    selectedReqId,
    setSelectedReqId,
    changeRequirementStatus,
    requirements,
    deleting,
  } = useRequirementsCard();
  const theme = useTheme();
  const title = quotation?.title || quotation?.name || "Requerimientos del sistema";
  const description = quotation?.description || "Sin descripción";
  const createdAt = quotation?.createdAt || quotation?.created_at || null;
  const updatedAt = quotation?.updatedAt || quotation?.updated_at || null;

  const createdFmt = useMemo(() => {
    if (!createdAt) return "—";
    const d = new Date(createdAt);
    return isNaN(d) ? "—" : d.toLocaleDateString("es-MX", { year: "numeric", month: "short", day: "2-digit" });
  }, [createdAt]);

  const updatedFmt = useMemo(() => {
    if (!updatedAt) return "—";
    const d = new Date(updatedAt);
    return isNaN(d) ? "—" : d.toLocaleDateString("es-MX", { year: "numeric", month: "short", day: "2-digit" });
  }, [updatedAt]);

  const canDelete = (req) => {
    const statusName = req?.status?.name?.toLowerCase?.();
    if (statusName === "confirmed") return false;
    if (viewerRole === "adevs-admin") return true;
    return viewerRole === "adevs-client" && req?.role === "adevs-client";
  };

  const canAccept = (req) =>
    viewerRole === "adevs-client" && (req?.status?.name || "").toLowerCase() === "created";

  const canCreate = () => {
    const statusName = quotation?.status?.name?.toLowerCase?.();
    if (statusName === "quoted" || statusName === "reviewing" || statusName === "rejected" || statusName === "client-rejected") {
      return false;
    }
    return true;
  };

  const selectedReq = requirements?.find((r) => (r?._id || r?.id) === selectedReqId);
  const deleteDisabled = !selectedReqId || !selectedReq || !canDelete(selectedReq);

  return (
    <StyledCardRequirements elevation={1}>
      {/* Header primary con acciones a la derecha */}
      <Box
        sx={{
          bgcolor: "primary.main",
          color: "primary.contrastText",
          py: 1.5,
          px: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6" color="primary.contrastText" sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
          <DescriptionIcon fontSize="small" color="primary.contrastText" /> {title}
        </Typography>
        <Stack direction="row" spacing={1}>
          {canCreate() && (
            <Tooltip title="Añadir requerimiento">
              <span>
                <IconButton style={{color: theme.palette.primary.contrastText}} onClick={() => openCreateModal()}>
                  <AddCircleOutlineIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title={deleteDisabled ? "Selecciona un requerimiento permitido para eliminar" : "Eliminar requerimiento seleccionado"}>
            <span>
              <IconButton
                style={{color: theme.palette.error.light}}
                disabled={deleteDisabled || deleting}
                onClick={() => openDeleteModal(selectedReqId)}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      <CardContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {description}
        </Typography>

        <Divider sx={{ my: 1 }} />

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Chip icon={<CalendarTodayIcon />} label={`Creada: ${createdFmt}`} size="small" />
          <Chip icon={<CalendarTodayIcon />} label={`Actualizada: ${updatedFmt}`} size="small" variant="outlined" />
        </Stack>

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
            <CategoryIcon fontSize="small" /> Requerimientos
          </Typography>

          {(!requirements || requirements.length === 0) ? (
            <Typography variant="body2" sx={{ color: "text.secondary", pl: 1 }}>
              No hay requerimientos aún. Usa “Añadir requerimiento”.
            </Typography>
          ) : (
            <List dense disablePadding>
              {requirements.map((req) => {
                const id = req?._id || req?.id;
                const isSelected = id === selectedReqId;
                const st = statusChip(req?.status?.name);
                const roleLabel = req?.role === "adevs-admin" ? "Admin" : req?.role === "adevs-client" ? "Cliente" : "—";

                return (
                  <ListItem
                    key={id}
                    onClick={() => setSelectedReqId(id)}
                    sx={{
                      borderRadius: 1,
                      px: 1,
                      cursor: "pointer",
                      bgcolor: isSelected ? "action.selected" : "transparent",
                      "&:hover": { bgcolor: "action.hover" },
                      alignItems: "flex-start",
                    }}
                    secondaryAction={
                      canAccept(req) ? (
                        <Tooltip title="Aceptar requerimiento">
                          <span>
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<CheckCircleOutlineIcon />}
                              onClick={async (e) => {
                                e.stopPropagation();
                                await changeRequirementStatus(id, "confirmed");
                              }}
                            >
                              Aceptar
                            </Button>
                          </span>
                        </Tooltip>
                      ) : null
                    }
                  >
                    <ListItemIcon sx={{ mt: 0.3 }}>
                      <DescriptionIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      disableTypography
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {req?.description || "(Sin descripción)"}
                          </Typography>
                          <Chip size="small" label={`De: ${roleLabel}`} variant="outlined" />
                          <Chip size="small" label={st.label} color={st.color} variant={st.variant} />
                        </Stack>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </Box>
      </CardContent>
    </StyledCardRequirements>
  );
};
